import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, startWith, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import { AppState } from 'app/types';
import User from 'entity/User';

import { ChatService } from '../../services/chat.service';
import { socialAppSelectors } from '../../state/app';

@Component({
  selector: 'app-social-chat-search',
  templateUrl: './social-chat-search.component.html',
  styleUrls: ['./social-chat-search.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialChatSearchComponent implements OnInit, OnDestroy {
  private chatService = inject(ChatService);
  private store = inject<Store<AppState>>(Store);

  public readonly searchValue = signal('');
  public readonly shouldShowOptions = signal(false);
  public readonly filteredParticipants = signal<User[]>([]);

  private readonly userId = signal<string | undefined>(undefined);
  private readonly users = signal<User[]>([]);
  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    this.store
      .select(coreSelectors.getUserId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userId) => this.userId.set(userId));
  }

  public ngOnInit(): void {
    this.store
      .select(socialAppSelectors.getScootaverseUsers)
      .pipe(startWith({}), takeUntil(this.destroyed$))
      .subscribe((scootaverseUsers) => {
        const fetchedUsers = Object.values(scootaverseUsers)
          .filter((user): user is User => !!user && user.userId !== this.userId())
          .sort((a, b) => formatUserName(a).localeCompare(formatUserName(b)));

        this.users.set(fetchedUsers);
        this.filteredParticipants.set(fetchedUsers);
        this.filterParticipants();
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public filterParticipants(): void {
    this.searchValue.set(this.searchValue().trim());
    if (!this.searchValue()) {
      this.shouldShowOptions.set(false);
      return;
    }

    this.filteredParticipants.set(
      this.users().filter((user) => {
        //using lowercase versions of search text and user's name for comparison.
        const searchValue = this.searchValue().toLowerCase();
        return formatUserName(user).toLowerCase().includes(searchValue);
      }),
    );
    this.shouldShowOptions.set(true);
  }

  public sendMessageTo(id: string): void {
    this.chatService.chatReceiver.next(id);
    this.searchValue.set('');
    this.filterParticipants();
  }

  public updateSearchValue(value: string): void {
    this.searchValue.set(value);
    this.filterParticipants();
  }
}
